import { cloneDeep, uniq } from 'lodash'

import { AssociatedMedias } from "./associated-medias"
import { SkinDate } from "./skin-date"
import { ThesaurusTerm } from "./thesaurus-term"
import { TranslatedString } from "./translated-string"
import { LifecycleStateResource, LinkableArchiveItemResource } from "./workflow-schema"

export const ConditionReportStatePrefix = 'ui.lifecycle-state.'
export enum ConditionReportState {
    IN_PROGRESS = 'inProgress',
    CLOSED = 'validated',
}

export enum ConditionReportValidationAction {
    UPDATE_ALL = 'updateAll',
    UPDATE_HISTORY = 'onlyHistory',
    DONT_UPDATE = 'nothing',
}

export interface Signer {
    id?: string
    signer?: TranslatedString
    signature?: string,
    positionOfSignatory?: string, // id of ThesaurusTerm
    dateOfSignature?: SkinDate,
}

export interface ConditionReport {
    id: string
    associatedMedias: AssociatedMedias
    conditionReportCS?: ConditionReportCSResource
    workflowSchema?: ConditionReportWorkflowSchemaResource
    conditioningSchema?: ConditioningSchemaResource
    transportSchema?: TransportSchemaResource
    mountingSchema?: MountingSchemaResource
}

interface ConditionReportCSResource {
    referenceNumber?: string
    reportDate?: SkinDate
    reportEditor?: ReportEditorResource[]
    reportReason?: string
    reportType?: string
    title?: TranslatedString
    signers?: SignerResource[]
    location?: TranslatedString
    technicsAndMaterials?: TranslatedString[]
    conditioning?: TranslatedString[] 
    contextOfExposition?: TranslatedString[]
    attachment?: TranslatedString[]
    comments?: TranslatedString[]
    stateOfConservations?: StateOfConservationResource[]
    generalState?: TranslatedString
    treatmentPriority?: string
    treatmentPriorityComment?: TranslatedString
    recommendations?: TranslatedString
    additionnalInformations?: TranslatedString
    alterationDetails?: AlterationDetailsResource[] | null
    integrities?: IntegrityResource[]
    predictInterventions?: PredictInterventionResource[]
    linkableItem?: LinkableArchiveItemResource

    alterations?: GenericAlterationResource[]
    examinationMethods?: ExaminationMethodResource[]
    analysisMethods?: AnalysisMethodResource[]
    restorationObjectives?: TranslatedString
    expectedResults?: TranslatedString
    treatmentsPerformed?: TreatmentsPerformedResource[]
    obtainedResults?: ObtainedResultsResource[]
    descriptionOfDamage?: TranslatedString
    counterSignatures?: CounterSignatureResource[]
}

export interface ReportEditorResource {
    reportName?: string // ref to alias
    precision?: TranslatedString
}

export interface SignerResource {
    signer?: TranslatedString
    signature?: string
    positionOfSignatory?: string
    dateOfSignature?: SkinDate
}

export interface StateOfConservationResource {
    stateOfConservation?: string
    typeStateOfConservation?: string
    precision?: TranslatedString
}

// Not used anymore
interface AlterationDetailsResource {
    element?: TranslatedString
    precision?: TranslatedString
    observationAlterationDetails?: ObservationAlterationDetailsResource[]
}

interface ObservationAlterationDetailsResource {
    typeOfObservation?: string
    materialAlterations?: MaterialAlterationResource[]
}

interface MaterialAlterationResource {
    material?: string
    alterations?: AlterationResource[]
}

interface AlterationResource {
    typeOfAlteration?: string
    precision?: TranslatedString
    annotations?: AnnotationResource[]
}

interface AnnotationResource {
    title?: TranslatedString
    comments?: TranslatedString[]
    media?: string
    svg?: string
}

interface IntegrityResource {
    integrity?: TranslatedString
    precision?: TranslatedString
}

export interface PredictInterventionResource {
    typeIntervention?: string
    precision?: TranslatedString
}

export interface GenericAlterationResource {
    index?: number // doesn't exist on Tryphon, used as key in list
    media?: string | null
    drawing?: string 
    title?: TranslatedString
    typeOfAlteration?: string // ThesaurusTerm id
    precision?: TranslatedString
    detailedViews?: string[]
}
export type Alteration = GenericAlterationResource // old name of GenericAlterationResource was Alteration
                                                   // TODO replace Alteration with GenericAlterationResource 
                                                   // where it is still used

interface ExaminationMethodResource {
    method?: TranslatedString
    furtherInformation?: TranslatedString
}

interface AnalysisMethodResource {
    method?: TranslatedString
    furtherInformation?: TranslatedString
}

interface TreatmentsPerformedResource {
    typeOfTreatment?: TranslatedString
    treatment?: TranslatedString
    furtherInformation?: TranslatedString
}

interface ObtainedResultsResource {
    results?: TranslatedString
    furtherInformation?: TranslatedString
}

export interface CounterSignatureResource {
    index?: number, // doesn't exist on Tryphon, used as key in list
    comment?: TranslatedString
    date?: SkinDate
    location?: TranslatedString
    reason?: string // thesaurus term id (parent thesaurus : "conditionReportMotif")
    observationCarriedPresenceOfs?: CounterSignerResource[]
}

// Name in Tryphon is ObservationCarriedPresenceOfResource but I find it confusing
export interface CounterSignerResource {
    index?: number, // doesn't exist on Tryphon, used as key in list
    surnameFirstName?: TranslatedString
    function?: string // thesaurus term id (parent thesaurus : "function")
    signature?: string
}

interface ConditionReportWorkflowSchemaResource {
    currentState?: LifecycleStateResource
}

interface ConditioningSchemaResource {
    typeOfPackaging?: TypeOfPackagingResource
    crateNumber?: TranslatedString
    transportAccessories?: TranslatedString[]
}

export interface TypeOfPackagingResource {
    type?: TranslatedString
    materials?: TranslatedString[]
    furtherInformations?: TranslatedString[]
}

interface TransportSchemaResource {
    carrierName?: string
    transportMode?: TranslatedString 
    presenceOfCourier?: Boolean 
    additionalEquipment?: AdditionalEquipmentResource
}

interface AdditionalEquipmentResource {
    shockIndicator?: TranslatedString
    vibrationIndicator?: TranslatedString 
    temperature?: TranslatedString
    relativeHumidity?: TranslatedString
}

interface MountingSchemaResource {
    typeOfMounting?: TranslatedString
    installationMode?: TranslatedString
    presentationAccessory?: TranslatedString
}

//                  //
//******UTILS*******//
//                  //
export const convertSignerToCounterSigner = (signer: SignerResource): CounterSignerResource => {
    return {
        surnameFirstName: signer.signer,
        function: signer.positionOfSignatory,
    } as CounterSignerResource
}

/**
 * 
 * @param conditionReport 
 * @returns 
 */
export const computeAlterationsIndexes = (conditionReport: ConditionReport): GenericAlterationResource[] => {
    let index = 1

    const medias = uniq(conditionReport?.associatedMedias?.medias) ?? []

    const alterationsCopy = cloneDeep(conditionReport?.conditionReportCS?.alterations) ?? []

    alterationsCopy.sort((alteration1, alteration2) => {
        if(alteration1.media && alteration2.media) {
            const alteration1MediaIndex = medias.findIndex(media => {
                return media === alteration1.media
            })

            const alteration2MediaIndex = medias.findIndex(media => {
                return media === alteration2.media
            })

            if(alteration1MediaIndex === alteration2MediaIndex) {
                return 0
            }

            // if index of alteration media equals -1 it means that the media was not found, 
            // we place the alteration at the very end
            if(alteration1MediaIndex === -1) {
                return -1
            }
            if(alteration2MediaIndex === -1) {
                return 1
            }

            // we sort alteration in the order of the medias they describe
            if(alteration1MediaIndex < alteration2MediaIndex) {
                return -1
            }
            if(alteration1MediaIndex > alteration2MediaIndex) {
                return 1
            }
        }

        // if an alteration is not linked to a media, we place it at the very end
        if(alteration1.media) {
            return -1
        }
        if(alteration2.media) {
            return 1
        }
        return 0
    })

    for(const alteration of alterationsCopy) {
        alteration.index = index
        index++
    }

    return alterationsCopy
}


/*
 * Legacy, deprecated
*/
export interface DefectAnnotation {
    id: number
    title: TranslatedString
    comments: TranslatedString[]
    media: string
    svg: string
}

export interface DefectAlteration {
    id: number
    typeOfAlteration: ThesaurusTerm['id']
    precision: TranslatedString
    annotations: DefectAnnotation[]
}

export interface DefectMaterial {
    id: number
    material: ThesaurusTerm['id']
    alterations: DefectAlteration[]
}

export interface DefectObservation {
    id: number
    typeOfObservation: ThesaurusTerm['id']
    materialAlterations: DefectMaterial[]
}

export interface DefectElement {
    id: number
    observationAlterationDetails: DefectObservation[]
    element: TranslatedString
    precision: TranslatedString
}